import { useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as TestIcon } from '@elementary/common-ui/assets/test-icon.svg';
import {
  Chip,
  CodeEditor,
  Flex,
  FlexCenter,
  FlexColumn,
  FormControls,
} from '@elementary/common-ui/components';
import {
  mapNameToColor,
  getFirstLettersOfName,
  capitalize,
} from '@elementary/common-ui/utils/common';
import { Typography } from '@mui/material';
import { UseFormWatch } from 'react-hook-form';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import ColumnsAcceptedValuesConfig from './WizardReviewStageConfigSections/ColumnsAcceptedValuesConfig';
import ColumnsNotNullConfig from './WizardReviewStageConfigSections/ColumnsNotNullConfig';
import ColumnsRelationshipConfig from './WizardReviewStageConfigSections/ColumnsRelationshipConfig';
import ColumnsUniqueConfig from './WizardReviewStageConfigSections/ColumnsUniqueConfig';
import CustomQueryConfig from './WizardReviewStageConfigSections/CustomQueryConfig';
import TablesDimensionAnomaliesConfig from './WizardReviewStageConfigSections/TablesDimensionAnomaliesConfig';
import TablesEventFreshnessAnomaliesConfig from './WizardReviewStageConfigSections/TablesEventFreshnessAnomaliesConfig';
import TablesFreshnessAnomaliesCloudConfig from './WizardReviewStageConfigSections/TablesFreshnessAnomaliesCloudConfig';
import TablesPipelineTaskPerformanceCloudConfig from './WizardReviewStageConfigSections/TablesPipelineTaskPerformanceCloudConfig';
import TablesFreshnessAnomaliesConfig from './WizardReviewStageConfigSections/TablesFreshnessAnomaliesConfig';
import TablesSchemaChangesAnomaliesConfig from './WizardReviewStageConfigSections/TablesSchemaChangesAnomaliesConfig';
import TablesVolumeAnomaliesCloudConfig from './WizardReviewStageConfigSections/TablesVolumeAnomaliesCloudConfig';
import TablesVolumeAnomaliesConfig from './WizardReviewStageConfigSections/TablesVolumeAnomaliesConfig';
import YamlTestConfig from './WizardReviewStageConfigSections/YamlTestConfig';
import { enforcementLevelOptions } from './constants';

const testNameMap = {
  freshnessAnomalies: 'Freshness anomalies',
  cloudFreshnessAnomalies: 'Freshness anomalies',
  eventFreshnessAnomalies: 'Event freshness anomalies',
  volumeAnomalies: 'Volume anomalies',
  cloudVolumeAnomalies: 'Volume anomalies',
  dimensionAnomalies: 'Dimension anomalies',
  schemaChanges: 'Schema changes',
  cloudDataContract: 'Data contract',
  cloudPipelineTaskPerformance: 'Pipeline task performance',
  unique: 'Unique',
  notNull: 'Not null',
  acceptedValues: 'Accepted values',
  relationship: 'Relationship',
  customQuery: 'Custom Query',
  yamlTest: 'Generic YAML Test',
  '': '',
};

const ItemChip = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.palette.common.gray11};
  padding: 8px 16px;
  border-radius: 6px;
  width: fit-content;
  gap: 8px;
  align-items: center;
`;

const Circle = styled(FlexCenter)<{ color: string }>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  border-radius: 100%;
  align-items: center;
  padding-right: 0.3px;
  background: ${({ color }) => color};
`;

const CloudChip = styled(Chip)`
  background-color: ${({ theme }) =>
    theme.palette.common.lightBlue4} !important;
  width: min-content;
`;

const ResourceChip = ({
  val,
  resourceType,
}: {
  val: Resource | ColumnResource;
  resourceType: string;
}) => (
  <ItemChip>
    <Typography
      sx={{ color: 'common.gray2' }}
    >{`${val.schema?.toLowerCase()}.`}</Typography>
    {resourceType === 'columns' && (
      <Typography sx={{ color: 'common.gray2' }}>{`${(
        val as ColumnResource
      ).model_name?.toLowerCase()}.`}</Typography>
    )}
    <Typography>{`${val.name?.toLowerCase()}`}</Typography>
  </ItemChip>
);

const OwnerLabel = ({ name }: { name: string }) => (
  <Flex sx={{ gap: 1, alignItems: 'center' }}>
    <Circle color={mapNameToColor(name)}>
      <Typography
        sx={{
          fontSize: '9px',
          fontWeight: 'bold',
          lineHeight: '8px',
          color: 'common.white',
        }}
      >
        {getFirstLettersOfName(name).toUpperCase()}
      </Typography>
    </Circle>
    <Typography>{name}</Typography>
  </Flex>
);

type Props = {
  watch: UseFormWatch<AddTestForm>;
  tableResourcesData?: Resource[];
  columnsResourcesData?: ColumnResource[];
};

const WizardReviewStage = ({
  watch,
  tableResourcesData = [],
  columnsResourcesData = [],
}: Props) => {
  const selectedResources = useMemo(
    () =>
      watch('resourceType') === 'tables'
        ? tableResourcesData.filter((r) => watch('resources')?.includes(r.id))
        : columnsResourcesData.filter((r) =>
            watch('resources')?.includes(r.id),
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsResourcesData, tableResourcesData, watch()],
  );

  const customQueryTestedTable = useMemo(() => {
    const table = tableResourcesData?.find(
      (resource) => watch('testedTable') === resource.id,
    );

    if (!table) {
      return '';
    }

    return table.source_name
      ? `${table.source_name}.${table.name}`
      : table.name;
  }, [tableResourcesData, watch]);

  return (
    <FlexColumn sx={{ gap: 2, p: 3 }}>
      {watch('testType') === 'customQuery' ? (
        <FormControls.TestAccordion
          summary={
            <Flex sx={{ gap: 1 }}>
              <Typography>Test query</Typography>
            </Flex>
          }
        >
          <FlexColumn sx={{ p: 3 }}>
            <CodeEditor
              height="250px"
              fontSize={14}
              mode="sql"
              value={watch('customQueryConverted')}
              readOnly
            />
          </FlexColumn>
        </FormControls.TestAccordion>
      ) : (
        <FormControls.TestAccordion
          summary={
            <Flex sx={{ gap: 1 }}>
              <Typography>Target</Typography>
              <Typography sx={{ color: 'common.gray12' }}>
                {`(${watch('resources')?.length} ${capitalize(
                  watch('resourceType'),
                )}
              selected)`}
              </Typography>
            </Flex>
          }
        >
          <FlexColumn sx={{ gap: 3, p: 3 }}>
            <FormControls.FieldsWrapper>
              <Typography sx={{ fontWeight: 'bold' }}>
                {`You've selected the following ${watch('resourceType')} (${
                  watch('resources')?.length
                }):`}
              </Typography>

              <Flex sx={{ gap: 1, flexWrap: 'wrap' }}>
                {selectedResources.map((r) => (
                  <ResourceChip
                    key={r.id}
                    val={r}
                    resourceType={watch('resourceType')}
                  />
                ))}
              </Flex>
            </FormControls.FieldsWrapper>
          </FlexColumn>
        </FormControls.TestAccordion>
      )}

      <FormControls.TestAccordion summary="Test configuration" defaultExpanded>
        <FlexColumn sx={{ gap: 3, p: 3 }}>
          <FormControls.FieldsWrapper>
            <Typography sx={{ fontWeight: 'bold' }}>
              You are adding the following tests (1):
            </Typography>

            <ItemChip>
              <Typography sx={{ lineHeight: '18px' }}>
                {testNameMap[watch('testType')]}
              </Typography>
              {watch('testType').startsWith('cloud') && (
                <CloudChip
                  label="elementary_cloud"
                  size="medium"
                  variant="outlined"
                  bold
                />
              )}
            </ItemChip>
          </FormControls.FieldsWrapper>

          <FormControls.FieldsWrapper>
            <Flex sx={{ gap: 1, alignItems: 'center' }}>
              <TestIcon />
              <Typography sx={{ fontWeight: 'bold' }}>Configuration</Typography>
            </Flex>

            {watch('testType') === 'freshnessAnomalies' ? (
              <TablesFreshnessAnomaliesConfig watch={watch} />
            ) : watch('testType') === 'eventFreshnessAnomalies' ? (
              <TablesEventFreshnessAnomaliesConfig watch={watch} />
            ) : watch('testType') === 'cloudFreshnessAnomalies' ? (
              <TablesFreshnessAnomaliesCloudConfig watch={watch} />
            ) : watch('testType') === 'volumeAnomalies' ? (
              <TablesVolumeAnomaliesConfig watch={watch} />
            ) : watch('testType') === 'cloudVolumeAnomalies' ? (
              <TablesVolumeAnomaliesCloudConfig watch={watch} />
            ) : watch('testType') === 'dimensionAnomalies' ? (
              <TablesDimensionAnomaliesConfig watch={watch} />
            ) : watch('testType') === 'schemaChanges' ? (
              <TablesSchemaChangesAnomaliesConfig watch={watch} />
            ) : watch('testType') === 'cloudPipelineTaskPerformance' ? (
              <TablesPipelineTaskPerformanceCloudConfig watch={watch} />
            ) : watch('testType') === 'cloudDataContract' ? (
              <FlexColumn sx={{ gap: 1 }}>
                <Typography>
                  Baseline mode:{' '}
                  <b>{capitalize(watch('baselineMode') || 'complete')}</b>
                </Typography>
                <Typography>
                  Enforcement level:{' '}
                  <b>
                    {
                      enforcementLevelOptions.find(
                        (o) => o.value === (watch('enforcementLevel') || 'all'),
                      )?.label
                    }
                  </b>
                </Typography>
              </FlexColumn>
            ) : watch('testType') === 'unique' ? (
              <ColumnsUniqueConfig watch={watch} />
            ) : watch('testType') === 'notNull' ? (
              <ColumnsNotNullConfig watch={watch} />
            ) : watch('testType') === 'acceptedValues' ? (
              <ColumnsAcceptedValuesConfig
                watch={watch}
                columnsResourcesData={columnsResourcesData}
              />
            ) : watch('testType') === 'relationship' ? (
              <ColumnsRelationshipConfig
                watch={watch}
                columnsResourcesData={columnsResourcesData}
              />
            ) : watch('testType') === 'customQuery' ? (
              <CustomQueryConfig watch={watch} />
            ) : watch('testType') === 'yamlTest' ? (
              <YamlTestConfig watch={watch} />
            ) : (
              <div />
            )}
          </FormControls.FieldsWrapper>

          {!watch('testType')?.startsWith('cloud') && (
            <>
              <FormControls.Divider />

              <FormControls.FieldsWrapper>
                <Typography sx={{ fontWeight: 'bold' }}>Description</Typography>
                <FormControls.FormText $muted={!watch('description')}>
                  {watch('description') || 'None'}
                </FormControls.FormText>
              </FormControls.FieldsWrapper>
            </>
          )}

          {watch('testType') === 'customQuery' && customQueryTestedTable ? (
            <FormControls.FieldsWrapper>
              <Typography sx={{ fontWeight: 'bold' }}>
                Advanced configuration
              </Typography>

              <FormControls.FormText>
                Location: <b>{watch('path')}</b>
              </FormControls.FormText>
              <FormControls.FormText>
                Quality dimension:
                <b>
                  {watch('qualityDimension')
                    ? watch('qualityDimension')
                    : 'Auto'}
                </b>
              </FormControls.FormText>
              <FormControls.FormText>
                Tested table: <b>{customQueryTestedTable}</b>
              </FormControls.FormText>
            </FormControls.FieldsWrapper>
          ) : (
            <div />
          )}

          <FormControls.FieldsWrapper>
            <Typography sx={{ fontWeight: 'bold' }}>Test metadata</Typography>
            <Flex sx={{ gap: 2 }}>
              <FormControls.FormText>Owners:</FormControls.FormText>
              {watch('owners')?.length ? (
                <Flex sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {watch('owners')?.map((name) => (
                    <OwnerLabel key={name} name={name} />
                  ))}
                </Flex>
              ) : (
                <FormControls.FormText $muted>None</FormControls.FormText>
              )}
            </Flex>

            <Flex sx={{ gap: 2 }}>
              <FormControls.FormText>Tags:</FormControls.FormText>
              {watch('tags')?.length ? (
                <Flex sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {watch('tags')?.map((val) => (
                    <FormControls.FormText
                      key={val}
                    >{`#${val}`}</FormControls.FormText>
                  ))}
                </Flex>
              ) : (
                <FormControls.FormText $muted>None</FormControls.FormText>
              )}
            </Flex>
          </FormControls.FieldsWrapper>
        </FlexColumn>
      </FormControls.TestAccordion>
    </FlexColumn>
  );
};

export default WizardReviewStage;
